/* Task-Management Phase 2 / C — checklist UI: the row/card {done}/{total} badge
 * and the task-drawer editor. The data logic (fetch/add/toggle/edit/reorder/
 * remove) lives on the Workspace instance (state/workspace.ts) and is
 * unit-tested there; this module is the lit rendering half (mirroring
 * ws/labels.ts).
 *
 * C1 (the load-bearing guardrail): a checklist is informational — checking every
 * item NEVER moves the task's status. The badge is a NEUTRAL `.pill-meta.count`
 * (Design archetype ruling: a 5/5 count must never read green/verified). */
import { html, nothing, type TemplateResult } from 'lit';
import type { Workspace } from '../state/workspace.ts';
import type { WsChecklistItem, WsTask } from '../types/workspace.ts';

/** The row/card mini-badge: a neutral `{done}/{total}` count, only when the task
 *  has checklist items. Deliberately quiet — it must not compete with the status
 *  pill or label chips for attention. */
export function checklistBadge(t: WsTask): TemplateResult | typeof nothing {
  const cl = t.checklist;
  if (!cl || !cl.total) return nothing;
  return html`<span class="pill-meta count wscl-badge"
    title="checklist — ${cl.done} of ${cl.total} done">☑ ${cl.done}/${cl.total}</span>`;
}

/** Pure reorder-position calculator (unit-tested): the new `position` for the
 *  item at `index` moving one slot in `dir` (−1 up / +1 down), midway between its
 *  new neighbours (Plane float spacing — no sibling renumbering). Returns null at
 *  the ends (nothing to do). */
export function reorderPosition(items: WsChecklistItem[], index: number, dir: -1 | 1): number | null {
  const j = index + dir;
  const item = items[j];
  if (j < 0 || j >= items.length || !item) return null; // !item unreachable past the bounds check
  if (dir === -1) {
    const target = item.position;
    const before = (j - 1 >= 0 ? items[j - 1]?.position : undefined) ?? target - 20000;
    return (target + before) / 2;
  }
  const target = item.position;
  const after = (j + 1 < items.length ? items[j + 1]?.position : undefined) ?? target + 20000;
  return (target + after) / 2;
}

/** The task-drawer checklist editor: a done-count header, checkable rows with
 *  inline edit + up/down reorder (keyboard-accessible) + remove, and an add
 *  input. `reload` re-renders the expander after any change (the same
 *  collapse+expand the note-save / labels editor use). Tokens only. */
export function checklistPanel(
  ws: Workspace, t: WsTask, items: WsChecklistItem[], reload: () => void,
): TemplateResult {
  const done = items.filter((i) => i.done).length;
  const total = items.length;
  const add = async (input: HTMLInputElement) => {
    const body = input.value.trim();
    if (!body) return;
    input.value = '';
    await ws.addChecklistItem(t, body);
    reload();
  };
  const toggle = async (item: WsChecklistItem) => { await ws.patchChecklistItem(t, item.id, { done: !item.done }); reload(); };
  const remove = async (item: WsChecklistItem) => { await ws.removeChecklistItem(t, item.id); reload(); };
  const edit = async (item: WsChecklistItem) => {
    const body = await ws.ask({ title: 'Edit checklist item', value: item.body, ok: 'Save' });
    if (body === null || !body.trim() || body.trim() === item.body) return;
    await ws.patchChecklistItem(t, item.id, { body: body.trim() });
    reload();
  };
  const move = async (index: number, dir: -1 | 1) => {
    const pos = reorderPosition(items, index, dir);
    const item = items[index];
    if (pos === null || !item) return; // pos non-null implies index in range
    await ws.patchChecklistItem(t, item.id, { position: pos });
    reload();
  };
  return html`<div class="wschecklist">
    <div class="wscl-head"><span class="wsllbl">Checklist</span>${total ? html`<span class="pill-meta count">${done}/${total}</span>` : nothing}</div>
    ${items.map((item, i) => html`
      <div class="wscl-item${item.done ? ' done' : ''}">
        <input type="checkbox" class="wscl-check" .checked=${!!item.done}
          aria-label=${item.done ? `mark "${item.body}" not done` : `mark "${item.body}" done`}
          @change=${() => toggle(item)}>
        <button class="wscl-body" title="click to edit" @click=${() => edit(item)}>${item.body}</button>
        <button class="wscl-mv" ?disabled=${i === 0} title="move up" aria-label="move up" @click=${() => move(i, -1)}>↑</button>
        <button class="wscl-mv" ?disabled=${i === items.length - 1} title="move down" aria-label="move down" @click=${() => move(i, 1)}>↓</button>
        <button class="wscl-rm" title="remove item" aria-label="remove item" @click=${() => remove(item)}>✕</button>
      </div>`)}
    <div class="wscl-add">
      <input class="wscl-input" type="text" placeholder="add a checklist item…"
        @keydown=${(ev: KeyboardEvent) => { if (ev.key === 'Enter') { ev.preventDefault(); add(ev.target as HTMLInputElement); } }}>
      <button class="wsmini wscl-addbtn" @click=${(ev: MouseEvent) => add((ev.currentTarget as HTMLElement).previousElementSibling as HTMLInputElement)}>Add</button>
    </div>
  </div>`;
}
